import { ComponentRef, Injectable, NgZone, OnDestroy } from "@angular/core";
import { Router } from "@angular/router";
import { ConnectedPosition, ConnectionPositionPair, Overlay, OverlayRef } from "@angular/cdk/overlay";
import { ComponentPortal } from "@angular/cdk/portal";
import { ViewportRuler } from "@angular/cdk/scrolling";
import { Subject, Subscription } from "rxjs";
import { filter, take, takeUntil } from "rxjs/operators";

import { ZoomService } from "@common/action-menu/zoom/zoom.service";
import { CoachMarkPromptComponent, CoachMarkPointerSide } from "@common/coach-mark/coach-mark-prompt.component";
import { BlockingUiSignalService } from "./blocking-ui-signal.service";
import { InteractionTrackingService, InteractionType } from "./interaction-tracking.service";
import {
  CoachMarkConfig,
  DEFAULT_COACH_MARK_POSITIONS,
  DESKTOP_ANCHORED_POSITIONS,
  isMobileViewport,
} from "./coach-mark.models";

/**
 * Maps a resolved CDK connection pair back to which side of the card the target sits on, so
 * the pointer triangle faces it. Covers DEFAULT_COACH_MARK_POSITIONS (below/above) and side
 * positions a custom `positions` array may use; anything else falls back to "top". A side placement
 * (the target's edge meeting the card's opposite edge) is recognised first, so it can still choose
 * its own overlayY - RIGHT_OF_TARGET_POSITIONS aligns the card's top, which alone reads as "below".
 */
function pointerSideFor(pair: ConnectionPositionPair): CoachMarkPointerSide {
  if (pair.originX === "end" && pair.overlayX === "start") return "left";
  if (pair.originX === "start" && pair.overlayX === "end") return "right";
  if (pair.overlayY === "top") return "top"; // overlay sits below the target
  if (pair.overlayY === "bottom") return "bottom"; // overlay sits above the target
  if (pair.overlayX === "start") return "left"; // overlay sits to the right of the target
  if (pair.overlayX === "end") return "right"; // overlay sits to the left of the target
  return "top";
}

/** "Waits up to 2s for the target" - the anchored-prompt contract (FLIP-340 / 343 / 344 / 347 / 348). */
const DEFAULT_RESOLVE_TIMEOUT_MS = 2000;
const RETRY_INTERVAL_MS = 150;
const PULSE_CLASS = "flip-coach-mark-pulse";
const PULSE_STATIC_CLASS = "flip-coach-mark-pulse--static";
/** Pane class for the unanchored fallback card - its bottom offset lives in cdk-theme.scss. */
const FALLBACK_PANEL_CLASS = "flip-coach-mark-fallback-panel";
/** Half the pointer's width (.ob-modal__pointer, 28px), to centre it on a stop. */
const POINTER_HALF_WIDTH_PX = 14;
/** The card's corner radius (.ob-modal__card) and the pointer's width (.ob-modal__pointer). */
const CARD_CORNER_RADIUS_PX = 15;
const POINTER_WIDTH_PX = 28;

/**
 * The pointer's `left` in px from the card's left edge, centred exactly on the target's horizontal
 * centre - clamped so the pointer stays on the card's straight edge, clear of its rounded corners.
 * (The Figma Anchored Prompt variants show five fixed stops; a target between two of them made the
 * pointer visibly miss, so placement is continuous instead.)
 */
export function centredPointerOffset(targetCentreX: number, cardLeft: number, cardWidth: number): number {
  const offset = Math.round(targetCentreX - cardLeft - POINTER_HALF_WIDTH_PX);
  const max = cardWidth - CARD_CORNER_RADIUS_PX - POINTER_WIDTH_PX;
  return Math.max(CARD_CORNER_RADIUS_PX, Math.min(offset, max));
}

/**
 * Whether a pointer on the card's straight edge can sit on the target's centre (see
 * centredPointerOffset) - false when the target lies beyond the pointer's clamp, i.e. out past the
 * card's corner clearance. A viewport-centred card in a mid-width window (between a phone and the
 * 768px desktop breakpoint) can fall short like this for a target near the edge.
 */
export function pointerReachesTarget(targetCentreX: number, cardLeft: number, cardWidth: number): boolean {
  const offset = Math.round(targetCentreX - cardLeft - POINTER_HALF_WIDTH_PX);
  return offset >= CARD_CORNER_RADIUS_PX && offset <= cardWidth - CARD_CORNER_RADIUS_PX - POINTER_WIDTH_PX;
}

/**
 * DEFAULT_COACH_MARK_POSITIONS with each offsetX shifting the card from the target's horizontal
 * centre to the viewport's, so the card always sits centred on screen, below (or above) the target.
 */
function viewportCentredPositions(
  target: HTMLElement,
  viewportWidth: number,
  extraClearancePx = 0,
  lockedSide?: "below" | "above"
): ConnectedPosition[] {
  const rect = target.getBoundingClientRect();
  const offsetX = Math.round(viewportWidth / 2 - (rect.left + rect.width / 2));
  const positions = lockedSide
    ? DEFAULT_COACH_MARK_POSITIONS.filter((position) =>
        lockedSide === "below" ? (position.offsetY ?? 0) > 0 : (position.offsetY ?? 0) < 0
      )
    : DEFAULT_COACH_MARK_POSITIONS;
  return positions.map((position) => ({
    ...position,
    offsetX,
    // offsetY is +24 below the target and -24 above it: extraClearancePx grows the magnitude
    // either way when positive, shrinks it (letting the card overlap the target) when negative.
    offsetY: (position.offsetY ?? 0) + Math.sign(position.offsetY ?? 0) * extraClearancePx,
  }));
}

/**
 * DESKTOP_ANCHORED_POSITIONS (tablet / desktop widths) with `lockedSide` and `extraClearancePx`
 * applied like viewportCentredPositions: a locked side keeps only that below / above entry, and the
 * clearance grows (or, negative, shrinks) each entry's gap to the target - offsetY for below / above,
 * offsetX for the beside-the-target fallbacks.
 */
function desktopAnchoredPositions(extraClearancePx = 0, lockedSide?: "below" | "above"): ConnectedPosition[] {
  const positions = lockedSide
    ? DESKTOP_ANCHORED_POSITIONS.filter((position) => position.originY === (lockedSide === "below" ? "bottom" : "top"))
    : DESKTOP_ANCHORED_POSITIONS;
  return positions.map((position) =>
    position.originY === "center"
      ? { ...position, offsetX: (position.offsetX ?? 0) + Math.sign(position.offsetX ?? 0) * extraClearancePx }
      : { ...position, offsetY: (position.offsetY ?? 0) + Math.sign(position.offsetY ?? 0) * extraClearancePx }
  );
}

/**
 * Fixed chrome docked over the bottom of the page: the `.action-menu` toolbar (back / zoom / undo /
 * save - action-menu.component.scss, itself docked above the bottom nav) and the mobile bottom nav
 * (`app-bottom-nav`, which also stacks Nova's tab above the nav bar). Either can be absent or
 * hidden (zero height - e.g. the side nav replaces the bottom nav on desktop).
 */
const BOTTOM_CHROME_SELECTORS = [".action-menu", "app-bottom-nav"];

/**
 * How much of the viewport's bottom edge is actually usable for content: above whichever fixed
 * bottom chrome is present and showing. Raw viewport height overstates the usable space - page
 * layouts pad their end clear of the nav (`--bottom-nav-height-padding`), but a target scrolled
 * only as far as the viewport edge still ends up underneath it, and so does a card below it.
 */
function usableViewportBottom(viewportHeight: number): number {
  return BOTTOM_CHROME_SELECTORS.reduce((bottom, selector) => {
    const rect = document.querySelector(selector)?.getBoundingClientRect();
    return rect && rect.height > 0 ? Math.min(bottom, rect.top) : bottom;
  }, viewportHeight);
}

/**
 * Full-screen modal backdrops that stack above the bottom chrome (z-index over the nav). A target
 * inside one is never under that chrome, however far down its modal runs - the Learn lesson modal's
 * 600px minimum height puts its footer behind the nav's footprint on a short phone.
 */
const ABOVE_BOTTOM_CHROME_SELECTORS = [".learn-modal-backdrop"];

/** `usableViewportBottom`, but the full viewport for a target inside a modal that covers the chrome. */
function usableBottomFor(target: HTMLElement, viewportHeight: number): number {
  return ABOVE_BOTTOM_CHROME_SELECTORS.some((selector) => target.closest(selector))
    ? viewportHeight
    : usableViewportBottom(viewportHeight);
}

/** Frames the target's rect must hold still before the follow loop stops (~330ms at 60fps). */
const SETTLE_STABLE_FRAMES = 20;
/** Upper bound on one follow run (~3s), so a target that animates forever can't keep it spinning. */
const SETTLE_MAX_FRAMES = 180;

/**
 * True when the target cannot be seen where it is even after scrolling: centred outside the
 * viewport, or under the fixed bottom chrome (a microphone in a sheet whose foot runs behind the nav
 * on a short viewport). A target that lives inside that chrome is, of course, fine.
 */
function targetIsObscured(target: HTMLElement, viewport: { width: number; height: number }): boolean {
  const rect = target.getBoundingClientRect();
  if (rect.width === 0 && rect.height === 0) return true;
  const centreX = rect.left + rect.width / 2;
  // A target taller than the viewport is left where it is (FLIP-485), so its centre is far below the
  // fold even though the part the prompt points at - its top - is in plain view: judge it by its top.
  const centreY = isTallTargetWithTopInView(target, viewport.height) ? rect.top : rect.top + rect.height / 2;
  if (centreX < 0 || centreX > viewport.width || centreY < 0 || centreY > viewport.height) return true;
  const insideChrome = BOTTOM_CHROME_SELECTORS.some((selector) => target.closest(selector));
  return !insideChrome && centreY > usableBottomFor(target, viewport.height);
}

/** Gap kept between the prompt card and the viewport's left / right edges (matches the overlay's own margin). */
const VIEWPORT_MARGIN_PX = 8;

/** Space kept between a scrolled-in target and the fixed bottom chrome. */
const TARGET_CLEARANCE_PX = 16;

/**
 * A target taller than the usable viewport (Learn's chapter grid, L01C) whose top is already in view.
 * It can never be cleared of the bottom chrome, and any scroll to "reveal" it - the browser's own
 * `scrollIntoView` aligns such a target's top to the screen edge just as the chrome scroll does -
 * leaves no room for a card above it (FLIP-485). Its top is in view, which is all the prompt needs.
 */
function isTallTargetWithTopInView(target: HTMLElement, viewportHeight: number): boolean {
  const rect = target.getBoundingClientRect();
  const usableBottom = usableBottomFor(target, viewportHeight);
  return rect.top >= 0 && rect.height > usableBottom - 2 * TARGET_CLEARANCE_PX;
}

/**
 * `scrollIntoView({ block: "nearest" })` brings a below-the-fold target only as far as the viewport's
 * bottom edge - underneath the fixed bottom nav / toolbar, so the prompt would point at something the
 * user can't see (SA03's ambient-audio player at the foot of Sanctuary). Scroll on just far enough
 * to clear that chrome, never so far that the target's top leaves the viewport.
 */
function scrollClearOfBottomChrome(target: HTMLElement, viewportHeight: number): void {
  if (isTallTargetWithTopInView(target, viewportHeight)) {
    return;
  }
  const rect = target.getBoundingClientRect();
  const usableBottom = usableBottomFor(target, viewportHeight);
  const overlap = rect.bottom + TARGET_CLEARANCE_PX - usableBottom;
  const scrollBy = Math.min(overlap, rect.top - TARGET_CLEARANCE_PX);
  if (scrollBy > 0) {
    scrollableAncestor(target)?.scrollBy({ top: scrollBy, behavior: "auto" });
  }
}

/**
 * The nearest ancestor that actually scrolls vertically (has overflow and scrollable content),
 * or `document.scrollingElement` (the window's real scroll host - Plan's own `.plan-container`
 * deliberately isn't one, see its `overflow-y: clip` comment) when none is found.
 */
function scrollableAncestor(el: HTMLElement): Element | null {
  let node = el.parentElement;
  while (node) {
    const style = getComputedStyle(node);
    if (/(auto|scroll)/.test(style.overflowY) && node.scrollHeight > node.clientHeight) {
      return node;
    }
    node = node.parentElement;
  }
  return document.scrollingElement;
}

/**
 * Where along the card's top/bottom edge the pointer sits, in px from the card's left: the
 * target's horizontal centre.
 */
function pointerOffsetFor(card: HTMLElement, target: HTMLElement): number {
  const cardRect = card.getBoundingClientRect();
  const targetRect = target.getBoundingClientRect();
  return centredPointerOffset(targetRect.left + targetRect.width / 2, cardRect.left, cardRect.width);
}
/**
 * FLIP-303. Generic anchored coach-mark engine on top of CDK Overlay - it knows nothing about
 * onboarding steps, only about targets (registered by `[flipCoachTarget]`, see
 * common/coach-mark/coach-mark-target.directive.ts), positions and fallbacks.
 *
 * Positioning mirrors card-action-menu.component.ts's `flexibleConnectedTo` usage, but with an
 * ordered position list (below, then above the target), the card horizontally centred on the
 * viewport with its pointer moved to face the target, viewport push, and CDK's `reposition()`
 * scroll strategy so the prompt
 * stays locked to a target inside a scrolling container (see the `cdkScrollable` registrations
 * on the Plan carousel and card-picker body).
 *
 * When the target never resolves, the same prompt (same copy, actions and close) renders as an
 * unanchored card at the bottom of the viewport instead - it never points at a missing target.
 */
@Injectable({ providedIn: "root" })
export class CoachMarkService implements OnDestroy {
  private readonly targets = new Map<string, HTMLElement>();
  private readonly destroyed$ = new Subject<void>();

  private overlayRef: OverlayRef | null = null;
  private pulseTarget: HTMLElement | null = null;
  private activeTeardown: (() => void) | null = null;

  constructor(
    private readonly overlay: Overlay,
    private readonly zone: NgZone,
    private readonly zoomService: ZoomService,
    private readonly blockingUiSignal: BlockingUiSignalService,
    private readonly interactionTracking: InteractionTrackingService,
    private readonly router: Router,
    private readonly viewportRuler: ViewportRuler
  ) {}

  /** Called by `FlipCoachTargetDirective` when its host mounts. */
  registerTarget(key: string, element: HTMLElement): void {
    this.targets.set(key, element);
  }

  /**
   * Called by `FlipCoachTargetDirective` when its host is destroyed. With `element`, only removes
   * the entry if it still points at that element - several elements can share one key (every card's
   * three-dot menu in a Plan, FLIP-386), and one being destroyed must not unregister another.
   */
  deregisterTarget(key: string, element?: HTMLElement): void {
    if (element && this.targets.get(key) !== element) return;
    this.targets.delete(key);
  }

  /** Whether a coach-mark is open, or still waiting for its target. */
  get isShowing(): boolean {
    return this.overlayRef !== null || this.activeTeardown !== null;
  }

  /**
   * Shows a coach-mark anchored to `config.target`, waiting out any active dialog/snackbar
   * first (see `BlockingUiSignalService`). Any previously shown coach-mark is dismissed.
   */
  show(config: CoachMarkConfig): void {
    this.dismiss();

    if (config.lessonId) {
      this.trackLesson("onboarding_lesson_state_changed", config.lessonId, "started");
    }

    if (config.showOverDialogs) {
      this.resolveTargetAndOpen(config);
      return;
    }

    this.blockingUiSignal.isBlockingUiActive$
      .pipe(
        filter((isActive) => !isActive),
        take(1),
        takeUntil(this.destroyed$)
      )
      .subscribe(() => this.resolveTargetAndOpen(config));
  }

  /** Tears down the current overlay / target wait, if any. */
  dismiss(): void {
    this.activeTeardown?.();
    this.activeTeardown = null;

    if (this.pulseTarget) {
      this.pulseTarget.classList.remove(PULSE_CLASS, PULSE_STATIC_CLASS);
      this.pulseTarget = null;
    }

    if (this.overlayRef) {
      this.overlayRef.dispose();
      this.overlayRef = null;
    }
  }

  ngOnDestroy(): void {
    this.dismiss();
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  private resolveTargetAndOpen(config: CoachMarkConfig): void {
    const timeoutMs = config.resolveTimeoutMs ?? DEFAULT_RESOLVE_TIMEOUT_MS;
    let settled = false;
    let retryHandle: ReturnType<typeof setTimeout> | null = null;
    let timeoutHandle: ReturnType<typeof setTimeout> | null = null;
    let resizeObserver: ResizeObserver | null = null;

    const cleanup = () => {
      if (retryHandle !== null) clearTimeout(retryHandle);
      if (timeoutHandle !== null) clearTimeout(timeoutHandle);
      resizeObserver?.disconnect();
    };

    const fallbackToCard = () => {
      if (settled) return;
      settled = true;
      cleanup();
      this.zone.run(() => this.openFallbackCard(config));
    };

    // The polling below runs outside Angular (no change detection per retry tick), so opening
    // must re-enter the zone: the prompt's button listeners are bound in whatever zone creates
    // them, and an action like "Add a star" that opens a MatDialog from outside the zone renders
    // a dialog that is never change-detected (no title, empty labels, no inline helpers).
    const attempt = () => {
      if (settled) return;
      const element = this.resolveElement(config.target);
      if (element?.isConnected) {
        settled = true;
        cleanup();
        this.zone.run(() => this.openOverlay(config, element));
        return;
      }
      retryHandle = setTimeout(attempt, RETRY_INTERVAL_MS);
    };

    // A timer independent of the retry loop, so a slow succession of retries can't push the
    // fallback out - it fires at exactly `timeoutMs` regardless of retry cadence.
    timeoutHandle = setTimeout(fallbackToCard, timeoutMs);

    // Catches a late-mounting target (picker card lists, carousel items) as soon as layout
    // settles, rather than waiting for the next fixed-interval retry tick.
    if (typeof ResizeObserver === "function") {
      resizeObserver = new ResizeObserver(() => attempt());
      resizeObserver.observe(document.body);
    }

    this.activeTeardown = cleanup;
    this.zone.runOutsideAngular(attempt);
  }

  /**
   * The target turned out to be unreachable on this screen (see targetIsObscured): replace the
   * anchored card with the unanchored fallback rather than point at empty space. The lesson has
   * already been reported as presented, so the fallback doesn't report it a second time.
   */
  private swapToFallback(config: CoachMarkConfig): void {
    this.activeTeardown?.();
    this.activeTeardown = null;
    if (this.pulseTarget) {
      this.pulseTarget.classList.remove(PULSE_CLASS, PULSE_STATIC_CLASS);
      this.pulseTarget = null;
    }
    this.overlayRef?.dispose();
    this.overlayRef = null;
    this.openFallbackCard(config, false);
  }

  private resolveElement(target: string | HTMLElement): HTMLElement | null {
    if (target instanceof HTMLElement) {
      return target;
    }
    return (
      this.targets.get(target) ??
      document.querySelector<HTMLElement>(`[data-flip-coach-target="${target}"]`)
    );
  }

  /**
   * The target never resolved: show the same prompt, unanchored (no pointer, no pulse), centred at
   * the bottom of the viewport - above the bottom nav where it shows (see
   * .flip-coach-mark-fallback-panel in cdk-theme.scss). The shell's own max-width keeps a 16px
   * gutter each side on narrow screens.
   */
  private openFallbackCard(config: CoachMarkConfig, reportPresented = true): void {
    this.overlayRef = this.overlay.create({
      positionStrategy: this.overlay
        .position()
        .global()
        .centerHorizontally()
        .bottom("var(--flip-coach-mark-fallback-bottom, 16px)"),
      scrollStrategy: this.overlay.scrollStrategies.noop(),
      hasBackdrop: false,
      panelClass: FALLBACK_PANEL_CLASS,
    });

    const componentRef = this.overlayRef.attach(new ComponentPortal(CoachMarkPromptComponent));
    componentRef.instance.pointerSide = undefined;

    if (config.lessonId) {
      const targetDetail = typeof config.target === "string" ? config.target : "element";
      this.trackLesson("onboarding_anchor_fallback", config.lessonId, `target: ${targetDetail}`);
    }

    const promptSub = this.wirePrompt(config, componentRef, {
      heading: config.fallback?.heading ?? config.heading,
      message: config.fallback?.message ?? config.message,
    }, reportPresented);
    componentRef.changeDetectorRef.detectChanges();

    this.activeTeardown = () => promptSub.unsubscribe();
  }

  private openOverlay(config: CoachMarkConfig, target: HTMLElement): void {
    // "Scrolls it into view (no animation where possible)" - before measuring, so the overlay
    // anchors to where the target ends up. Guarded for test DOMs that lack it. Always the minimal
    // "nearest" scroll here, even with a lockedSide: pulling the target all the way to the
    // viewport edge over-scrolls past content that already had enough room, surfacing
    // reserved-but-offscreen layout space below it (e.g. the Plan carousel's bottom offset) that
    // was never meant to show. "nearest" only guarantees the target is visible though, not that
    // the card has room to render on its locked side - the positionSub callback below tops that
    // up with a second, precisely-measured scroll once the card's real height is known.
    // `config.scrollToTarget === false` skips every scroll below: for a target the user has just
    // tapped (CA02's open card menu), where scrolling would leave it - and any fixed overlay it
    // belongs to - behind on the page.
    const mayScroll = config.scrollToTarget !== false;
    // Measured before any scroll. A tall target with its top in view is left where it is: "nearest"
    // would align its top to the screen edge, an unnecessary scroll (FLIP-485: L01C chapter grid).
    const leaveInPlace = isTallTargetWithTopInView(target, this.viewportRuler.getViewportSize().height);
    if (mayScroll && !leaveInPlace && typeof target.scrollIntoView === "function") {
      target.scrollIntoView({ block: "nearest", inline: "nearest", behavior: "auto" });
    }
    // "nearest" stops at the viewport edge - finish the job so the target is clear of the fixed
    // bottom nav / toolbar too.
    if (mayScroll) {
      scrollClearOfBottomChrome(target, this.viewportRuler.getViewportSize().height);
    }

    // No explicit `positions`: re-derived on every resize / zoom, since crossing the 768px
    // breakpoint switches between the viewport-centred card (mobile) and one near its target.
    // A viewport-centred card whose pointer can't reach the target (measured below) switches to the
    // target-relative placements too; reposition() clears it so a resize gets a fresh try.
    let pointerMissedTarget = false;
    const centred = !config.positions;
    // CDK applies a position's offsetX as a transform *after* its push-into-viewport step, so a card
    // whose offset carries it past a viewport edge is never pulled back (FLIP-485: UP07A Back, -40px
    // from a target ~20px off the left edge, ended up 18px off screen). The handler below measures the
    // card and nudges it in by `edgeShift`, which is folded into every position's offsetX.
    let edgeShift = 0;
    const basePositions = (): ConnectedPosition[] =>
      config.positions ??
      (isMobileViewport() && !pointerMissedTarget
        ? viewportCentredPositions(
            target,
            this.viewportRuler.getViewportSize().width,
            config.extraClearancePx,
            config.lockedSide
          )
        : config.desktopPositions ?? desktopAnchoredPositions(config.extraClearancePx, config.lockedSide));
    const positionsNow = (): ConnectedPosition[] =>
      edgeShift === 0
        ? basePositions()
        : basePositions().map((position) => ({ ...position, offsetX: (position.offsetX ?? 0) + edgeShift }));
    const positions = positionsNow();

    const positionStrategy = this.overlay
      .position()
      .flexibleConnectedTo(target)
      .withPositions(positions)
      .withPush(true)
      .withViewportMargin(VIEWPORT_MARGIN_PX);

    this.overlayRef = this.overlay.create({
      positionStrategy,
      scrollStrategy: this.overlay.scrollStrategies.reposition(),
      hasBackdrop: false,
    });

    const componentRef = this.overlayRef.attach(new ComponentPortal(CoachMarkPromptComponent));
    const promptSub = this.wirePrompt(config, componentRef, { heading: config.heading, message: config.message });

    // The strategy only reports a change once it has actually resolved a position, so the card
    // renders with the "top" default for one frame on first open - matches the target being
    // above the card in the common case (DEFAULT_COACH_MARK_POSITIONS tries below it first).
    // Every applied position is re-measured, so the pointer follows the target through viewport
    // push and scroll repositioning as well as the centring offset.
    const MAX_ROOM_ATTEMPTS = 3;
    const MAX_EDGE_ATTEMPTS = 2;
    let edgeAttempts = 0;
    // No scrolling to make room either when the config opted out of scrolling.
    let roomAttempts = mayScroll ? 0 : MAX_ROOM_ATTEMPTS;
    let edgeFrame: number | null = null;
    const scheduleEdgeCheck = () => {
      if (edgeAttempts >= MAX_EDGE_ATTEMPTS || typeof requestAnimationFrame !== "function") return;
      if (edgeFrame !== null) cancelAnimationFrame(edgeFrame);
      edgeFrame = requestAnimationFrame(() => {
        edgeFrame = null;
        if (!this.overlayRef || edgeAttempts >= MAX_EDGE_ATTEMPTS) return;
        const card: HTMLElement | null = componentRef.location.nativeElement.querySelector(".ob-modal");
        if (!card) return;
        const cardRect = card.getBoundingClientRect();
        const viewportWidth = this.viewportRuler.getViewportSize().width;
        const shift =
          cardRect.left < VIEWPORT_MARGIN_PX
            ? VIEWPORT_MARGIN_PX - cardRect.left
            : cardRect.right > viewportWidth - VIEWPORT_MARGIN_PX
            ? viewportWidth - VIEWPORT_MARGIN_PX - cardRect.right
            : 0;
        if (Math.abs(shift) < 1 || cardRect.width > viewportWidth - 2 * VIEWPORT_MARGIN_PX) return;
        edgeAttempts++;
        edgeShift += Math.round(shift);
        this.zone.run(() => {
          positionStrategy.withPositions(positionsNow());
          this.overlayRef?.updatePosition(); // emits again, re-measuring the pointer with the card back inside
        });
      });
    };
    const positionSub = positionStrategy.positionChanges
      .pipe(takeUntil(this.destroyed$))
      .subscribe((change) => {
        const side = pointerSideFor(change.connectionPair);
        componentRef.instance.pointerSide = side;
        // Measured against .ob-modal - the pointer's positioning context - not the inline host.
        const card: HTMLElement =
          componentRef.location.nativeElement.querySelector(".ob-modal") ?? componentRef.location.nativeElement;
        componentRef.instance.pointerOffset =
          side === "top" || side === "bottom"
            ? pointerOffsetFor(card, target)
            : undefined;
        componentRef.changeDetectorRef.detectChanges();

        // Keep the card inside the viewport's side margins (see edgeShift) - checked a frame later,
        // once CDK has finished applying (and pushing) this position.
        scheduleEdgeCheck();

        // Measured after change detection, once the card has its real width. The pointer is clamped to
        // the card's straight edge, so a viewport-centred card can't always point at the target.
        if (centred && isMobileViewport() && !pointerMissedTarget && (side === "top" || side === "bottom")) {
          const cardRect = card.getBoundingClientRect();
          const targetRect = target.getBoundingClientRect();
          if (!pointerReachesTarget(targetRect.left + targetRect.width / 2, cardRect.left, cardRect.width)) {
            pointerMissedTarget = true;
            positionStrategy.withPositions(positionsNow());
            this.overlayRef?.updatePosition(); // emits again with the new placement
            return;
          }
        }

        // "nearest" only guarantees the target itself is visible, not that the card has room to
        // render below (or above) it without being pushed off its locked side. Scroll just far
        // enough to fit the card's actual rendered height - not further, so no reserved-but-
        // offscreen space gets revealed by scrolling past what's actually needed (the earlier
        // over-scroll bug this replaces). A single measurement can still undershoot if the card's
        // layout (text wrap, fonts) hadn't fully settled at that read, so re-check and top up
        // against the latest rects each time, capped so a persistently-tight viewport can't loop.
        if (roomAttempts < MAX_ROOM_ATTEMPTS && (side === "top" || side === "bottom")) {
          const targetRect = target.getBoundingClientRect();
          const cardRect = card.getBoundingClientRect();
          const viewportBottom = usableViewportBottom(this.viewportRuler.getViewportSize().height);
          const clearancePx = 16;
          // The card doesn't start flush against the target - there's the configured offsetY gap
          // (or overlap, if extraClearancePx made it negative) first. Read it back off the actual
          // resolved position rather than re-deriving it, so this stays correct however it was
          // computed (extraClearancePx sign and magnitude alike).
          const gapPx = Math.abs(
            positions.find((p) => p.overlayY === change.connectionPair.overlayY)?.offsetY ?? 24
          );
          const deficit =
            side === "top"
              ? gapPx + cardRect.height + clearancePx - (viewportBottom - targetRect.bottom)
              : gapPx + cardRect.height + clearancePx - targetRect.top;
          if (deficit > 0) {
            roomAttempts++;
            scrollableAncestor(target)?.scrollBy({ top: side === "top" ? deficit : -deficit, behavior: "auto" });
            this.overlayRef?.updatePosition();
          } else {
            roomAttempts = MAX_ROOM_ATTEMPTS;
          }
        }
      });

    // A resize or zoom moves the viewport's centre relative to the target - re-centre, then
    // reposition (which re-measures the pointer above).
    const reposition = () => {
      // A fresh measurement: the target may have moved, so the edge nudge is worked out again.
      edgeShift = 0;
      edgeAttempts = 0;
      if (centred) {
        pointerMissedTarget = false;
      }
      positionStrategy.withPositions(positionsNow());
      this.overlayRef?.updatePosition();
    };

    const prefersReducedMotion =
      typeof matchMedia === "function" && matchMedia("(prefers-reduced-motion: reduce)").matches;
    target.classList.add(PULSE_CLASS);
    target.classList.toggle(PULSE_STATIC_CLASS, prefersReducedMotion);
    this.pulseTarget = target;

    const zoomSub = this.zoomService.zoomLevel$
      .pipe(takeUntil(this.destroyed$))
      .subscribe(() => reposition());
    const resizeSub = this.viewportRuler
      .change(100)
      .pipe(takeUntil(this.destroyed$))
      .subscribe(() => this.zone.run(() => reposition()));

    // The centring offset above is computed from getBoundingClientRect() reads, and the first one
    // is taken right as the prompt opens - often straight after a dialog just closed, or while a
    // sheet is still sliding in (Nova's chat animates up from the bottom, so its microphone is
    // measured well below its resting place), or before fonts/images have settled. On a fast phone
    // that has all finished by the next frame; on a slower one it hasn't, and nothing else corrects
    // the prompt until an unrelated resize happens. So follow the target frame by frame until its
    // rect has stopped changing, and again whenever the visual viewport (iOS toolbar collapsing),
    // the page or the target itself moves.
    let torn = false;
    let frameHandle: number | null = null;
    let framesRun = 0;
    let stableFrames = 0;
    let checkedObscured = false;
    const targetKey = () => {
      const r = target.getBoundingClientRect();
      return `${Math.round(r.left)}|${Math.round(r.top)}|${Math.round(r.width)}|${Math.round(r.height)}`;
    };
    let lastKey = targetKey();

    const step = () => {
      frameHandle = null;
      if (torn) return;
      framesRun++;
      const key = targetKey();
      // The first frame always repositions - layout has definitely settled by then.
      if (framesRun === 1 || key !== lastKey) {
        lastKey = key;
        stableFrames = 0;
        this.zone.run(() => reposition());
      } else {
        stableFrames++;
      }
      if (stableFrames < SETTLE_STABLE_FRAMES && framesRun < SETTLE_MAX_FRAMES) {
        frameHandle = requestAnimationFrame(step);
        return;
      }
      if (!checkedObscured) {
        checkedObscured = true;
        if (targetIsObscured(target, this.viewportRuler.getViewportSize())) {
          this.zone.run(() => this.swapToFallback(config));
        }
      }
    };
    // (Re)starts the follow loop; a loop already running just has its stability count reset.
    const follow = () => {
      if (torn || typeof requestAnimationFrame !== "function") return;
      stableFrames = 0;
      framesRun = Math.min(framesRun, SETTLE_MAX_FRAMES - SETTLE_STABLE_FRAMES);
      if (frameHandle === null) {
        frameHandle = requestAnimationFrame(step);
      }
    };
    follow();

    const visualViewport = typeof window !== "undefined" ? window.visualViewport : null;
    this.zone.runOutsideAngular(() => {
      visualViewport?.addEventListener("resize", follow);
      visualViewport?.addEventListener("scroll", follow);
      window.addEventListener("scroll", follow, { capture: true, passive: true });
    });
    let targetObserver: ResizeObserver | null = null;
    if (typeof ResizeObserver === "function") {
      targetObserver = new ResizeObserver(() => follow());
      targetObserver.observe(target);
    }

    this.activeTeardown = () => {
      torn = true;
      if (frameHandle !== null && typeof cancelAnimationFrame === "function") {
        cancelAnimationFrame(frameHandle);
      }
      visualViewport?.removeEventListener("resize", follow);
      visualViewport?.removeEventListener("scroll", follow);
      window.removeEventListener("scroll", follow, { capture: true });
      targetObserver?.disconnect();
      if (edgeFrame !== null && typeof cancelAnimationFrame === "function") {
        cancelAnimationFrame(edgeFrame);
      }
      positionSub.unsubscribe();
      promptSub.unsubscribe();
      zoomSub.unsubscribe();
      resizeSub.unsubscribe();
    };
  }

  /**
   * Shared by the anchored and fallback paths, so both render the same prompt and behave the same:
   * copy and button labels, tracking (`presented`, each button, close), `onDismiss` defer
   * semantics, and self-dismissal after any button. The returned subscription tears it down.
   */
  private wirePrompt(
    config: CoachMarkConfig,
    componentRef: ComponentRef<CoachMarkPromptComponent>,
    copy: { heading: string; message: string },
    reportPresented = true
  ): Subscription {
    const prompt = componentRef.instance;
    prompt.heading = copy.heading;
    prompt.message = copy.message;
    prompt.primaryLabel = config.primaryAction?.label;
    prompt.secondaryLabel = config.secondaryAction?.label;

    if (config.lessonId && reportPresented) {
      this.trackLesson("onboarding_lesson_presented", config.lessonId);
    }

    const subs = new Subscription();

    subs.add(
      prompt.dismissed.pipe(take(1), takeUntil(this.destroyed$)).subscribe(() => {
        if (config.lessonId) {
          this.trackLesson("onboarding_lesson_action", config.lessonId, "dismiss button");
          if (!config.onDismiss) {
            this.trackLesson("onboarding_lesson_state_changed", config.lessonId, "dismissed");
          }
        }
        this.runThenDismiss(config.onDismiss);
      })
    );

    subs.add(
      prompt.primaryAction.pipe(take(1), takeUntil(this.destroyed$)).subscribe(() => {
        if (config.lessonId) {
          this.trackLesson("onboarding_lesson_action", config.lessonId, `primary button: ${config.primaryAction?.label}`);
        }
        this.runThenDismiss(config.primaryAction?.onClick);
      })
    );

    subs.add(
      prompt.secondaryAction.pipe(take(1), takeUntil(this.destroyed$)).subscribe(() => {
        if (config.lessonId) {
          this.trackLesson("onboarding_lesson_action", config.lessonId, `secondary button: ${config.secondaryAction?.label}`);
        }
        this.runThenDismiss(config.secondaryAction?.onClick);
      })
    );

    return subs;
  }

  /**
   * Runs a prompt's own close / button callback, then dismisses the prompt - unless the callback
   * moved on to another one. A lesson that hands over to the next (N03A -> N03B, FLIP-384) calls
   * show() from inside its callback; show() has already torn this prompt down, so dismissing now
   * would close the prompt that was just opened.
   */
  private runThenDismiss(callback?: () => void): void {
    const current = this.activeTeardown;
    callback?.();
    if (this.activeTeardown === current) {
      this.dismiss();
    }
  }

  /**
   * FLIP-322. Only called when `config.lessonId` is set - non-onboarding callers of this
   * generic engine stay untracked. `detail` carries the lesson id via the same "marker: value"
   * convention as other tracked events, since `entityId` is numeric and lesson ids are strings.
   */
  private trackLesson(eventName: string, lessonId: string, detail?: string): void {
    this.interactionTracking.track({
      screen: this.router.url,
      eventName,
      interactionType: InteractionType.Selection,
      entityType: "OnboardingLesson",
      detail: detail ? `lesson: ${lessonId}; ${detail}` : `lesson: ${lessonId}`,
    });
  }
}
